"use client"

import { AuthGuard } from '@/components/auth-guard'
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  IconBuilding,
  IconBuildingStore,
  IconMapPin,
  IconWorld,
  IconDeviceMobile,
  IconMail,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandTelegram,
  IconAlertCircle,
  IconCheck,
  IconLoader2,
  IconPhoto,
} from "@tabler/icons-react"
import { useToast } from "@/components/ui/toast"

// Country list with codes
const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "IN", name: "India" },
  { code: "JP", name: "Japan" },
  { code: "CN", name: "China" },
  { code: "SG", name: "Singapore" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "ZA", name: "South Africa" },
  { code: "NZ", name: "New Zealand" },
  { code: "KR", name: "South Korea" },
  { code: "MY", name: "Malaysia" },
  { code: "TH", name: "Thailand" },
  { code: "ID", name: "Indonesia" },
  { code: "PH", name: "Philippines" },
  { code: "VN", name: "Vietnam" },
  { code: "HK", name: "Hong Kong" },
  { code: "TW", name: "Taiwan" },
  { code: "CH", name: "Switzerland" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "DK", name: "Denmark" },
  { code: "NO", name: "Norway" },
  { code: "SE", name: "Sweden" },
  { code: "FI", name: "Finland" },
  { code: "PL", name: "Poland" },
  { code: "CZ", name: "Czech Republic" },
  { code: "GR", name: "Greece" },
  { code: "PT", name: "Portugal" },
  { code: "IE", name: "Ireland" },
  { code: "RU", name: "Russia" },
  { code: "TR", name: "Turkey" },
  { code: "IL", name: "Israel" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "EG", name: "Egypt" },
  { code: "NG", name: "Nigeria" },
  { code: "KE", name: "Kenya" },
  { code: "AR", name: "Argentina" },
  { code: "CO", name: "Colombia" },
  { code: "PE", name: "Peru" },
  { code: "CL", name: "Chile" },
  { code: "VE", name: "Venezuela" },
  { code: "UY", name: "Uruguay" },
  { code: "PY", name: "Paraguay" },
  { code: "EC", name: "Ecuador" },
  { code: "BO", name: "Bolivia" },
  { code: "GT", name: "Guatemala" },
  { code: "CU", name: "Cuba" },
  { code: "JM", name: "Jamaica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "PR", name: "Puerto Rico" },
  { code: "PA", name: "Panama" },
  { code: "CR", name: "Costa Rica" },
]

// Common timezone list
const TIMEZONES = [
  { value: "America/New_York", label: "(GMT-05:00) Eastern Time (US & Canada)" },
  { value: "America/Chicago", label: "(GMT-06:00) Central Time (US & Canada)" },
  { value: "America/Denver", label: "(GMT-07:00) Mountain Time (US & Canada)" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) Pacific Time (US & Canada)" },
  { value: "America/Anchorage", label: "(GMT-09:00) Alaska Time" },
  { value: "Pacific/Honolulu", label: "(GMT-10:00) Hawaii Time" },
  { value: "America/Phoenix", label: "(GMT-07:00) Mountain Time (no DST)" },
  { value: "Europe/London", label: "(GMT+00:00) Greenwich Mean Time" },
  { value: "Europe/Paris", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Berlin", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Rome", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Madrid", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Amsterdam", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Brussels", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Vienna", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Zurich", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Stockholm", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Copenhagen", label: "(GMT+01:00) Central European Time" },
  { value: "Europe/Helsinki", label: "(GMT+02:00) Eastern European Time" },
  { value: "Europe/Moscow", label: "(GMT+03:00) Moscow Time" },
  { value: "Asia/Dubai", label: "(GMT+04:00) Gulf Standard Time" },
  { value: "Asia/Tehran", label: "(GMT+03:30) Iran Standard Time" },
  { value: "Asia/Tel_Aviv", label: "(GMT+02:00) Israel Standard Time" },
  { value: "Asia/Kolkata", label: "(GMT+05:30) India Standard Time" },
  { value: "Asia/Bangkok", label: "(GMT+07:00) Indochina Time" },
  { value: "Asia/Singapore", label: "(GMT+08:00) Singapore Time" },
  { value: "Asia/Hong_Kong", label: "(GMT+08:00) Hong Kong Time" },
  { value: "Asia/Tokyo", label: "(GMT+09:00) Japan Standard Time" },
  { value: "Asia/Seoul", label: "(GMT+09:00) Korea Standard Time" },
  { value: "Asia/Shanghai", label: "(GMT+08:00) China Standard Time" },
  { value: "Asia/Taipei", label: "(GMT+08:00) China Standard Time" },
  { value: "Asia/Jakarta", label: "(GMT+07:00) Western Indonesia Time" },
  { value: "Asia/Manila", label: "(GMT+08:00) Philippine Standard Time" },
  { value: "Asia/Kuala_Lumpur", label: "(GMT+08:00) Malaysia Time" },
  { value: "Pacific/Auckland", label: "(GMT+12:00) New Zealand Time" },
  { value: "Australia/Sydney", label: "(GMT+10:00) Australian Eastern Time" },
  { value: "Australia/Melbourne", label: "(GMT+10:00) Australian Eastern Time" },
  { value: "Europe/Istanbul", label: "(GMT+03:00) Turkey Time" },
  { value: "America/Sao_Paulo", label: "(GMT-03:00) Brasilia Time" },
  { value: "America/Buenos_Aires", label: "(GMT-03:00) Argentine Time" },
  { value: "America/Santiago", label: "(GMT-04:00) Chile Time" },
  { value: "America/Bogota", label: "(GMT-05:00) Colombia Time" },
  { value: "America/Mexico_City", label: "(GMT-06:00) Central Time (Mexico)" },
]
import {
  getCompanyInfo,
  updateCompanyInfo,
} from "@/lib/api-client"

interface CompanyInfo {
  id: string
  business_name?: string
  contact_name?: string
  email?: string
  phone?: string
  logo_url?: string
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  website?: string
  industry?: string
  description?: string
  tax_id?: string
  facebook?: string
  instagram?: string
  whatsapp?: string
  telegram?: string
  timezone?: string
  updated_at: string
  created_at: string
}

export default function CompanyInfoPage() {
  const { addToast } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    website: "",
    industry: "",
    description: "",
    tax_id: "",
    facebook: "",
    instagram: "",
    whatsapp: "",
    telegram: "",
    timezone: "America/New_York",
  })

  // Load company info on mount
  useEffect(() => {
    loadCompanyInfo()
  }, [])

  const loadCompanyInfo = async () => {
    try {
      setLoading(true)
      const data = await getCompanyInfo()
      setCompanyInfo(data)

      // Populate form with existing data
      setFormData({
        business_name: data.business_name || "",
        contact_name: data.contact_name || "",
        email: data.email || "",
        phone: data.phone || "",
        address: data.address || "",
        city: data.city || "",
        state: data.state || "",
        postal_code: data.postal_code || "",
        country: data.country || "US",
        website: data.website || "",
        industry: data.industry || "",
        description: data.description || "",
        tax_id: data.tax_id || "",
        facebook: data.facebook || "",
        instagram: data.instagram || "",
        whatsapp: data.whatsapp || "",
        telegram: data.telegram || "",
        timezone: data.timezone || "America/New_York",
      })
    } catch (error: any) {
      console.error('Failed to load company info:', error)
      addToast({
        title: "Error",
        message: error?.message || "Failed to load company information",
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)

      // Only send fields that have values
      const updateData: any = {}
      Object.entries(formData).forEach(([key, value]) => {
        if (value && value.trim() !== "") {
          updateData[key] = value
        }
      })

      const response = await updateCompanyInfo(updateData)

      setCompanyInfo(prev => prev ? { ...prev, ...updateData, updated_at: new Date().toISOString() } : null)
      setHasChanges(false)

      // Success toast with API response details
      addToast({
        title: "Saved successfully",
        message: response?.message || "Your company information has been updated",
      })
    } catch (error: any) {
      console.error('Failed to update company info:', error)

      // Error toast with API error message
      addToast({
        title: "Save failed",
        message: error?.message || "Failed to update company information",
        variant: "error",
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <AuthGuard>
        <div className="flex items-center justify-center h-64">
          <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AuthGuard>
    )
  }

  return (
    <AuthGuard>
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Company Info</h1>
            <p className="text-muted-foreground mt-2">
              Manage your business information and contact details
            </p>
          </div>
          {hasChanges && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="animate-pulse">
                Unsaved changes
              </Badge>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <IconCheck className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <Tabs defaultValue="basic" className="space-y-6">
          <TabsList>
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="address">Address</TabsTrigger>
            <TabsTrigger value="social">Social Media</TabsTrigger>
            <TabsTrigger value="details">Business Details</TabsTrigger>
          </TabsList>

          {/* Basic Information */}
          <TabsContent value="basic">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IconBuilding className="h-5 w-5" />
                  Basic Information
                </CardTitle>
                <CardDescription>
                  Your company's core contact information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={formData.business_name}
                      onChange={(e) => handleInputChange("business_name", e.target.value)}
                      placeholder="Acme Corporation"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => handleInputChange("contact_name", e.target.value)}
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="contact@acme.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Address */}
          <TabsContent value="address">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IconMapPin className="h-5 w-5" />
                  Business Address
                </CardTitle>
                <CardDescription>
                  Your company's physical location
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      placeholder="New York"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                      placeholder="NY"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postal_code">Postal Code</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange("postal_code", e.target.value)}
                      placeholder="10001"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => handleInputChange("country", value)}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => handleInputChange("timezone", value)}
                  >
                    <SelectTrigger id="timezone">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Media */}
          <TabsContent value="social">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IconWorld className="h-5 w-5" />
                  Online Presence
                </CardTitle>
                <CardDescription>
                  Your company's website and social media links
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="website" className="flex items-center gap-2">
                    <IconWorld className="h-4 w-4" />
                    Website
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange("website", e.target.value)}
                    placeholder="https://www.acme.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="facebook" className="flex items-center gap-2">
                    <IconBrandFacebook className="h-4 w-4" />
                    Facebook
                  </Label>
                  <Input
                    id="facebook"
                    type="url"
                    value={formData.facebook}
                    onChange={(e) => handleInputChange("facebook", e.target.value)}
                    placeholder="https://facebook.com/acme"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="instagram" className="flex items-center gap-2">
                    <IconBrandInstagram className="h-4 w-4" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    type="url"
                    value={formData.instagram}
                    onChange={(e) => handleInputChange("instagram", e.target.value)}
                    placeholder="https://instagram.com/acme"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telegram" className="flex items-center gap-2">
                    <IconBrandTelegram className="h-4 w-4" />
                    Telegram
                  </Label>
                  <Input
                    id="telegram"
                    type="url"
                    value={formData.telegram}
                    onChange={(e) => handleInputChange("telegram", e.target.value)}
                    placeholder="https://t.me/acme"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whatsapp" className="flex items-center gap-2">
                    <IconDeviceMobile className="h-4 w-4" />
                    WhatsApp
                  </Label>
                  <Input
                    id="whatsapp"
                    value={formData.whatsapp}
                    onChange={(e) => handleInputChange("whatsapp", e.target.value)}
                    placeholder="+1234567890"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Business Details */}
          <TabsContent value="details">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IconBuildingStore className="h-5 w-5" />
                  Business Details
                </CardTitle>
                <CardDescription>
                  Additional information about your business
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    value={formData.industry}
                    onChange={(e) => handleInputChange("industry", e.target.value)}
                    placeholder="Retail, Restaurant, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Tell us about your business..."
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tax_id">Tax ID / EIN</Label>
                  <Input
                    id="tax_id"
                    value={formData.tax_id}
                    onChange={(e) => handleInputChange("tax_id", e.target.value)}
                    placeholder="12-3456789"
                  />
                  <p className="text-sm text-muted-foreground">
                    Your business tax identifier (optional)
                  </p>
                </div>

                {companyInfo?.logo_url && (
                  <Alert>
                    <IconPhoto className="h-4 w-4" />
                    <AlertDescription>
                      Current logo: <a href={companyInfo.logo_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">View Logo</a>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {companyInfo && (
          <div className="text-sm text-muted-foreground">
            Last updated: {new Date(companyInfo.updated_at).toLocaleString()}
          </div>
        )}
      </div>
    </AuthGuard>
  )
}
